/* ═══════════════════════════════════════════════════
   LOGIN PAGE
   File: src/components/Auth/Login.js
═══════════════════════════════════════════════════ */

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loginUser } from "../../utils/firebase";
import { useAuth } from "../../utils/AuthContext";

const CSS = `
  .pf-auth { min-height: 100vh; padding: 120px 0 80px; display: flex; align-items: center; justify-content: center; position: relative; }
  .pf-auth-bg {
    position: absolute; inset: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 45% 40% at 20% 40%, rgba(255,107,53,0.05) 0%, transparent 55%),
      radial-gradient(ellipse 40% 40% at 80% 60%, rgba(0,212,170,0.04) 0%, transparent 50%);
  }
  .pf-auth-card {
    width: 100%; max-width: 420px; padding: 40px 36px; border-radius: 24px;
    background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06);
    position: relative; z-index: 1;
    animation: fadeInUp 0.6s ease both;
  }
  .pf-auth-icon { text-align: center; font-size: 2.5em; margin-bottom: 12px; display: block; }
  .pf-auth-title {
    font-family: 'DM Serif Display', serif; font-size: 1.6em; color: #F0F0F5;
    text-align: center; margin-bottom: 6px;
  }
  .pf-auth-sub { font-size: 0.86em; color: rgba(240,240,245,0.35); text-align: center; margin-bottom: 28px; }

  .pf-auth-field { margin-bottom: 18px; }
  .pf-auth-label { display: block; font-size: 0.78em; font-weight: 600; color: rgba(240,240,245,0.4); margin-bottom: 6px; }
  .pf-auth-input {
    width: 100%; padding: 13px 16px; border-radius: 14px;
    background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.08);
    color: #F0F0F5; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.92em;
    outline: none; transition: all 0.3s ease;
  }
  .pf-auth-input::placeholder { color: rgba(240,240,245,0.2); }
  .pf-auth-input:focus {
    border-color: rgba(255,107,53,0.4);
    box-shadow: 0 0 0 3px rgba(255,107,53,0.08);
    background: rgba(255,255,255,0.06);
  }

  .pf-auth-submit {
    width: 100%; padding: 14px; border-radius: 50px; border: none;
    background: linear-gradient(135deg, #FF6B35, #FF8A5C);
    color: #fff; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 0.95em;
    cursor: pointer; transition: all 0.3s ease;
    box-shadow: 0 6px 24px rgba(255,107,53,0.3);
    margin-top: 8px;
  }
  .pf-auth-submit:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(255,107,53,0.45); }
  .pf-auth-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .pf-auth-error {
    padding: 10px 14px; border-radius: 12px; margin-bottom: 16px;
    background: rgba(255,77,106,0.08); border: 1px solid rgba(255,77,106,0.2);
    font-size: 0.82em; color: #FF4D6A; text-align: center;
  }

  .pf-auth-footer { text-align: center; margin-top: 20px; font-size: 0.84em; color: rgba(240,240,245,0.3); }
  .pf-auth-footer a { color: #FF6B35; font-weight: 600; text-decoration: none; }
  .pf-auth-footer a:hover { color: #FFD23F; }

  .pf-auth-divider { display: flex; align-items: center; gap: 12px; margin: 20px 0; }
  .pf-auth-divider::before, .pf-auth-divider::after { content: ''; flex: 1; height: 1px; background: rgba(255,255,255,0.06); }
  .pf-auth-divider span { font-size: 0.72em; color: rgba(240,240,245,0.2); font-weight: 600; }

  @keyframes fadeInUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
`;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // If already logged in, redirect to dashboard
  if (user) {
    navigate("/dashboard");
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await loginUser(email, password);
      navigate("/dashboard");
    } catch (err) {
      // Friendly error messages
      const code = err.code;
      if (code === "auth/user-not-found")
        setError("No account found with this email. Try signing up!");
      else if (code === "auth/wrong-password")
        setError("Incorrect password. Please try again.");
      else if (code === "auth/invalid-email")
        setError("Please enter a valid email address.");
      else if (code === "auth/too-many-requests")
        setError("Too many attempts. Please wait a minute and try again.");
      else setError("Login failed. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{CSS}</style>
      <section className="pf-auth">
        <div className="pf-auth-bg" />
        <div className="pf-auth-card">
          <span className="pf-auth-icon">🔑</span>
          <h1 className="pf-auth-title">Welcome Back</h1>
          <p className="pf-auth-sub">Log in to see your saved career results</p>

          {error && <div className="pf-auth-error">{error}</div>}

          <form onSubmit={handleLogin}>
            <div className="pf-auth-field">
              <label className="pf-auth-label">Email Address</label>
              <input
                type="email"
                className="pf-auth-input"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="pf-auth-field">
              <label className="pf-auth-label">Password</label>
              <input
                type="password"
                className="pf-auth-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <button type="submit" className="pf-auth-submit" disabled={loading}>
              {loading ? "Logging in..." : "🚀 Log In"}
            </button>
          </form>

          <div className="pf-auth-divider">
            <span>OR</span>
          </div>

          <div className="pf-auth-footer">
            Don't have an account? <Link to="/signup">Sign Up Free</Link>
          </div>
        </div>
      </section>
    </>
  );
}
